import { brand } from '@avexa/config/brand'
import { logoGeometry } from '@avexa/config/logo'
import { useId } from 'react'

import { cn } from '../../lib/cn'

/**
 * The Proffplassen lockup: the client's mark, plus the wordmark.
 *
 * ⚠️ SUPPLIED, NOT INVENTED — AND THE GEOMETRY IS NOT IN THIS FILE.
 *
 * The client sent artwork on 6 Oct 2026 (`Images/Logo/logo-light.jpeg`,
 * `logo-dark.jpeg`). Both are JPEGs, so neither has an alpha channel and the
 * light one is a white box on any coloured surface; the mark is therefore
 * redrawn as SVG. Every coordinate and colour lives in
 * `packages/config/src/logo.ts`, which is also what generates the favicon, the
 * manifest icons and the mobile app icons — so the mark exists ONCE.
 *
 * ⚠️ That file's header records the open trademark question: two overlapping
 * circles with a lighter intersection is structurally the Mastercard mark, and
 * this product is adding payments. Read it before changing anything here.
 *
 * ─── WHY THE WORDMARK IS SENTENCE CASE ──────────────────────────────────────
 *
 * Because the artwork is. Both supplied files set it as "Proffplassen". The
 * September onboarding mockups set the OLD name in capitals, which is why
 * `{{productUpper}}` still exists for copy — but a logotype follows the logo,
 * not the copy, and "PROFFPLASSEN" is twelve capitals where "AVEXA" was five.
 *
 * ─── WHY THE SIZES ARE LITERAL AND DO NOT USE `type-*` ───────────────────────
 *
 * `type-h3` grows from 18 px to 22 px at `md`, and the desktop header is the one
 * place in this app with a measured width budget (see Header.tsx — the nav row
 * came within 15 px of the 1280 px breakpoint in Norwegian with the five-letter
 * name). A brand mark that grows on the widest layout is the opposite of what
 * that budget wants, so the lockup is one fixed size everywhere: ~145 px wide.
 *
 * The wordmark comes from `@avexa/config/brand`, not from a catalogue: it is a
 * proper noun, identical in `en` and `nb`. Copy that *mentions* the product uses
 * `{{product}}`.
 */

type LogoProps = {
  className?: string
  /** Hides the wordmark, leaving only the mark. Used in tight chrome. */
  markOnly?: boolean
}

const { width, height, left, right } = logoGeometry

/** Rendered height of the mark, in px. Width follows from the 77:52 aspect. */
const MARK_HEIGHT = 26
const MARK_WIDTH = Math.round((MARK_HEIGHT * width) / height)

export const Logo = ({ className, markOnly = false }: LogoProps) => {
  // Namespaced per instance: the Header renders a Logo and the mobile sheet
  // renders another, and two identical `clipPath` ids in one document is a
  // silent correctness bug the day one of them stops being identical.
  const clipId = `${useId()}-logo-clip`

  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
        width={MARK_WIDTH}
        height={MARK_HEIGHT}
      >
        <defs>
          <clipPath id={clipId}>
            <circle cx={left.cx} cy={left.cy} r={left.r} />
          </clipPath>
        </defs>
        <circle cx={left.cx} cy={left.cy} r={left.r} className="fill-logo-mark" />
        <circle cx={right.cx} cy={right.cy} r={right.r} className="fill-logo-mark-alt" />
        <circle
          cx={right.cx}
          cy={right.cy}
          r={right.r}
          clipPath={`url(#${clipId})`}
          className="fill-logo-mark-overlap"
        />
      </svg>

      {markOnly ? null : (
        <span className="whitespace-nowrap text-[17px] font-extrabold leading-none tracking-tight text-logo">
          {brand.productName}
        </span>
      )}
    </span>
  )
}
